import { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import useStore from '../../store/useStore';

let lenisInstance: Lenis | null = null;

/**
 * Initialise Lenis smooth scroll and pipe scroll progress into the Zustand
 * store. Intended to be called once in App.tsx via `useScrollEngine`.
 *
 * Also auto-triggers dark mode when scroll passes `darkModeThreshold`.
 */
export function useScrollEngine(): void {
    const rafRef = useRef<number>(0);
    const { setScrollProgress, setIsDark } = useStore.getState().methods;

    useEffect(() => {
        lenisInstance = new Lenis({
            lerp: 0.08,
            smoothWheel: true,
        });

        const onScroll = ({ progress }: { progress: number }) => {
            setScrollProgress(progress);

            const threshold = useStore.getState().darkModeThreshold;
            const currentDark = useStore.getState().isDark;
            const shouldBeDark = progress >= threshold;
            if (shouldBeDark !== currentDark) {
                setIsDark(shouldBeDark);
            }
        };

        lenisInstance.on('scroll', onScroll);

        const raf = (time: number) => {
            lenisInstance?.raf(time);
            rafRef.current = requestAnimationFrame(raf);
        };
        rafRef.current = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafRef.current);
            lenisInstance?.destroy();
            lenisInstance = null;
        };
    }, [setScrollProgress, setIsDark]);
}

/** Imperative access to Lenis for programmatic scrolling. */
export function getLenis(): Lenis | null {
    return lenisInstance;
}
